import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import isEqual from 'lodash/isEqual';
import cx from 'classnames';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl-next';
import window from 'window-or-global';

import { token } from '@atlaskit/tokens';
import Popup from '@atlaskit/popup';

import { A11Y_CONTENT_SUFFIX } from '@confluence/comments-util/entry-points/commentsConstants';
import { ReactionsContext } from '@confluence/comment-context';
import { updateCacheForReactions as updateCommentCacheForReactions } from '@confluence/comments-section/entry-points/updateCache';
import { Reactions, ReactionLocation, ReactionContainerType } from '@confluence/reactions';
import {
	DELETE_PAGE_COMMENT_EXPERIENCE,
	// WARN: disabling only to meet ESlint requirements during ESlint upgrade
	getExperienceTracker, // eslint-disable-line no-restricted-imports
	stopExperienceOnError,
} from '@confluence/experience-tracker';
import { END } from '@confluence/navdex';

import { confirm } from '../../tech-debt/facades/window';

// eslint-disable-next-line no-restricted-syntax, @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import './CommentActions.css';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './CommentActions.module.css';

const i18n = defineMessages({
	replyLinkA11yAriaLabel: {
		id: 'comment.a11y.link.reply',
		defaultMessage:
			'Reply to {isCurrentUserComment, select, true {your own comment} other {comment by {fullName}}} from {timeAgo}',
		description: 'Label that announced by screen reader for reply link in comment',
	},
	editLinkA11yAriaLabel: {
		id: 'comment.a11y.link.edit',
		defaultMessage:
			'Edit {isCurrentUserComment, select, true {your own comment} other {comment by {fullName}}} from {timeAgo}',
		description: 'Label that announced by screen reader for edit link in comment',
	},
	deleteLinkA11yAriaLabel: {
		id: 'comment.a11y.link.delete',
		defaultMessage:
			'Delete {isCurrentUserComment, select, true {your own comment} other {comment by {fullName}}} from {timeAgo}',
		description: 'Label that announced by screen reader for delete link in comment',
	},
});

/* eslint-disable jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
export default injectIntl(
	class CommentActions extends Component {
		static displayName = 'CommentActions';
		static propTypes = {
			currentUserAccountId: PropTypes.string,
			/**
			 * @see {@link Comment.propTypes.comment}
			 */
			comment: PropTypes.object,
			/**
			 * The content id that the comment belongs to
			 */
			contentId: PropTypes.string,
			/**
			 * Content Type of the page
			 */
			contentType: PropTypes.oneOf(['blogpost', 'page']),
			/**
			 * SpaceId of current space
			 */
			spaceId: PropTypes.string.isRequired,
			/**
			 * i18n keys and is injected by injectIntl decorator
			 */
			intl: PropTypes.object,
			/**
			 * Action to delete a comment for a contentId
			 */
			deleteComment: PropTypes.func,
			/**
			 * Actions dispatcher to show editor fo replying to comment
			 */
			replyToComment: PropTypes.func,
			/**
			 * Actions dispatcher to show editor for current comment
			 */
			editComment: PropTypes.func,
			/**
			 * A boolean indicating whether or not edit is restricted due to a legacy comment
			 */
			restrictEdit: PropTypes.bool,
			/**
			 * Reply comment Permission
			 */
			canReplyToComment: PropTypes.bool.isRequired,
			/**
			 * A boolean indicating should copy link button be shown
			 */
			showCopyLinkButton: PropTypes.bool.isRequired,
			/**
			 * A function to send analytics events
			 */
			createAnalyticsEvent: PropTypes.func.isRequired,
		};

		constructor() {
			super();
			this._onDeleteClick = this._onDeleteClick.bind(this);
			this._onReplyClick = this._onReplyClick.bind(this);
			this._onEditClick = this._onEditClick.bind(this);

			this.state = {
				dialogShown: false,
			};
		}

		shouldComponentUpdate(nextProps, nextState) {
			return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
		}

		_handleDialogClose = () => {
			this.setState({ dialogShown: false });
		};

		_handleDialogLinkClick = () => {
			const {
				comment: { id },
				replyToComment,
			} = this.props;

			this.setState({ dialogShown: false });

			// Reply to the comment they were trying to edit to keep context
			replyToComment(id);
		};

		_onReplyClick(e) {
			e.preventDefault();
			const { comment, replyToComment } = this.props;
			replyToComment(comment.id);
		}

		_onEditClick(e) {
			e.preventDefault();
			const {
				comment: { id },
				editComment,
				restrictEdit,
			} = this.props;

			if (restrictEdit) {
				this.setState({ dialogShown: true });
			} else {
				editComment(id);
			}
		}

		_onDeleteClick() {
			const { contentId, spaceId } = this.props;
			const { formatMessage } = this.props.intl;

			//Start delete Page Comment Experience
			getExperienceTracker().start({
				name: DELETE_PAGE_COMMENT_EXPERIENCE,
			});

			if (confirm(formatMessage({ id: 'remove.comment.confirmation.message' }))) {
				const {
					deleteComment,
					comment: { id: commentId },
				} = this.props;

				deleteComment(commentId).then(
					() => {
						getExperienceTracker().succeed({
							name: DELETE_PAGE_COMMENT_EXPERIENCE,
						});

						this.props
							.createAnalyticsEvent({
								type: 'sendTrackEvent',
								data: {
									source: 'viewPageScreen',
									containerType: 'space',
									containerId: spaceId,
									objectType: 'page',
									objectId: contentId,
									action: 'deleted',
									actionSubject: 'comment',
									actionSubjectId: commentId,
									attributes: {
										commentType: 'page',
										mode: 'view',
										context: 'default',
										navdexPointType: END,
									},
								},
							})
							.fire();
					},
					(error) => {
						stopExperienceOnError(DELETE_PAGE_COMMENT_EXPERIENCE, error);
					},
				);
			} else {
				getExperienceTracker().abort({
					name: DELETE_PAGE_COMMENT_EXPERIENCE,
					reason: 'Delete Comment dialog closed',
				});
			}
		}

		_renderEditDialogContent() {
			return (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				<div className={styles.editDisabledDialogWrapper}>
					<a
						onClick={this._handleDialogLinkClick}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={styles.editDisabledDialogLink}
					>
						<FormattedMessage id="comment.edit.disable.link" defaultMessage="Add a new comment " />
					</a>
					<FormattedMessage
						id="comment.edit.disable.explanation"
						// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
						// eslint-disable-next-line no-restricted-syntax
						defaultMessage="since this one can't be edited; it contains content created in a legacy version of Confluence."
					/>
				</div>
			);
		}

		_renderReactions(comment, contentId, contentType) {
			const { id, reactionsSummary } = comment;

			return (
				<Fragment>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<span className={cx('reactions-spacer', styles.reactionsSpacer)} />
					<li
						key={id}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={cx(
							'comment-action-reaction',
							styles.actionItem,
							styles.noDot,
							styles.actionItemReactions,
						)}
						data-testid="comment-actions-reactions"
					>
						<Reactions
							contentId={id}
							containerId={contentId}
							containerType={
								contentType === 'page' ? ReactionContainerType.PAGE : ReactionContainerType.BLOGPOST
							}
							location={ReactionLocation.PAGE_COMMENT}
							reactionsData={reactionsSummary}
							updateCache={updateCommentCacheForReactions}
						/>
					</li>
				</Fragment>
			);
		}
		_getCommentLink() {
			const { origin } = window.location;
			return `${origin}/wiki${this.props.comment.linkWebui}`;
		}

		render() {
			const { contentId, contentType, comment, canReplyToComment, restrictEdit, intl } = this.props;
			const { dialogShown } = this.state;
			const { id } = comment;

			//comment operations

			const isEditCommentAllowed =
				!!comment.operations.find((op) => op.operation === 'update') ||
				(comment.permissions && comment.permissions.isEditable);

			const isDeleteCommentAllowed =
				!!comment.operations.find((op) => op.operation === 'delete') ||
				(comment.permissions && comment.permissions.isRemovable);

			const commentActionLinkAriaLabelInfo = {
				isCurrentUserComment: this.props.currentUserAccountId === comment.createdBy.accountId,
				fullName: comment.createdBy.displayName,
				timeAgo: comment.createdFriendly,
			};

			return (
				<ReactionsContext.Consumer>
					{({ isReactionsEnabled }) => (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						<div className={styles.actions}>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
							<ul className={styles.actionsListWithReactions}>
								{canReplyToComment && (
									<li
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
										className={cx(styles.actionItem, 'action-reply-comment')}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
										style={{ marginTop: token('space.050', '4px') }}
									>
										<span
											id={`${id}-replyLinkTranslatedAriaLabel`}
											// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
											style={{ display: 'none' }}
										>
											{intl.formatMessage(
												i18n.replyLinkA11yAriaLabel,
												commentActionLinkAriaLabelInfo,
											)}
										</span>
										<a
											data-testId={`${id}-comment-reply-link`}
											// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
											className="comment-reply-link"
											onClick={this._onReplyClick}
											href=""
											aria-labelledby={`${id}-replyLinkTranslatedAriaLabel ${id}-${A11Y_CONTENT_SUFFIX}`}
										>
											<FormattedMessage id="comment.reply" />
										</a>
									</li>
								)}
								{isEditCommentAllowed ? (
									<Popup
										onClose={this._handleDialogClose}
										placement="bottom-start"
										// eslint-disable-next-line react/jsx-no-bind
										content={() => this._renderEditDialogContent()}
										isOpen={dialogShown}
										// eslint-disable-next-line react/jsx-no-bind
										trigger={(triggerProps) => (
											<li
												// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
												className={cx(styles.actionItem, 'comment-action-edit')}
											>
												<span
													id={`${id}-editLinkTranslatedAriaLabel`}
													// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
													style={{ display: 'none' }}
												>
													{intl.formatMessage(
														i18n.editLinkA11yAriaLabel,
														commentActionLinkAriaLabelInfo,
													)}
												</span>
												<a
													data-testId={`${id}-comment-edit-link`}
													// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
													className="comment-edit-link"
													onClick={this._onEditClick}
													href=""
													aria-labelledby={`${id}-editLinkTranslatedAriaLabel ${id}-${A11Y_CONTENT_SUFFIX}`}
													{...triggerProps}
												>
													{restrictEdit ? (
														<FormattedMessage
															id="comment.edit.not.available"
															defaultMessage="Edit is not available"
															description="Disabling Edit for Legacy comments with Unsupported content"
														/>
													) : (
														<FormattedMessage id="comment.edit" />
													)}
												</a>
											</li>
										)}
									/>
								) : null}
								{isDeleteCommentAllowed ? (
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									<li className={styles.actionItem}>
										<span
											id={`${id}-deleteLinkTranslatedAriaLabel`}
											// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
											style={{ display: 'none' }}
										>
											{intl.formatMessage(
												i18n.deleteLinkA11yAriaLabel,
												commentActionLinkAriaLabelInfo,
											)}
										</span>
										<a
											data-test-id="comment-delete-link"
											onClick={this._onDeleteClick}
											id={`remove-comment-${comment.id}`}
											aria-labelledby={`${id}-deleteLinkTranslatedAriaLabel ${id}-${A11Y_CONTENT_SUFFIX}`}
											href=""
										>
											<FormattedMessage id="comment.remove" />
										</a>
									</li>
								) : null}
								{isReactionsEnabled && this._renderReactions(comment, contentId, contentType)}
							</ul>
						</div>
					)}
				</ReactionsContext.Consumer>
			);
		}
	},
);
